import React, { useEffect, useRef, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { push } from 'connected-react-router';

import {
  Column,
  Paging,
  SearchPanel,
  Sorting,
  FilterPanel,
  HeaderFilter,
  FilterBuilder,
  Lookup,
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';

import {
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Button
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import { withStyles } from '@material-ui/core/styles';

import isPlainObject from 'lodash/isPlainObject';

import { columnSchema } from './gridConfig';
import {
  setChannelWorkflowGridStateUpdate,
  createExploreSystemView,
  appNotify,
  setIsRedirectToExplore,
  fetchOpportunityHistoryLogDetails,
  setEventSummaryRowData,
} from 'store/actions';
import { importModuleStateSelector } from 'store/selectors';
import { dataGridExtension } from 'components/devExtreme';
import DataGrid from 'components/devExtreme/DataGrid';
import CustomColumnChooser from 'components/customColumnChooser';
import { sendClientLog } from 'services/logService';
import { Constants } from 'utilities/constants';
import { GetAsgPropIdsWithImportId } from 'api/revue/import';
import { getWithToken, postWithToken } from 'api/network';
import ViewOpportunityHistoryDetailsModal from 'components/underwrite/acquisition/propertyUnderwrite/header/ViewOpportunityHistoryDetailsModal';
import { Tooltip as DevExtremeTooltip } from 'devextreme-react/tooltip';

const {
  Routes: { CHANNEL_WORKFLOW, EXPLORE },
  Module: { UNDERWRITE },
} = Constants;

const VIEW_EXPLORE = 'View Explore';
const STRING = 'string';
const VIEW_WORKFLOW = 'View Workflow';
const ID = 'id';

const styles = theme => ({
  gridPaper: {
    padding: '5px 8px 8px',
    overflow: 'hidden',
    width: '100%'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    // width: '66%'
  },
  title: {
    margin: '5px 0 8px 0'
  },
  refreshButton: {
    marginLeft: 8
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  },
  flexContainer: {
    display: 'flex'
  }
});


const ImportOpportunityHistoryGrid = ({
  classes,
  setChannelWorkflowGridStateUpdate,
  push,
  createExploreSystemView,
  loading,
  setLoading,
  appSettings,
  appNotify,
  importHistoryRefresh,
  setIsRedirectToExplore,
  fetchOpportunityHistoryLogDetails,
  setEventSummaryRowData,
}) => {
  const [dataSource, setDataSource] = useState(null);
  const [channelLookupDataSource, setChannelLookupDataSource] = useState(null);
  const [isColumnChooserVisible, setIsColumnChooserVisible] = useState(false);

  const gridRef = useRef(null);
  const gridId = 'importOpportunityHistoryGrid';

  useEffect(() => {
    createDataSource();
    createChannelLookupDataSource();
    setLoading({ isLoading: true, message: 'Fetching Import Opportunity History...' });
  }, [importHistoryRefresh]);

  function createDataSource() {
    const url = '/api/underwrite/import/history';

    const store = new CustomStore({
      key: ID,
      load: function (loadOptions) {
        if (isPlainObject(loadOptions.group)) {
          loadOptions.group = [loadOptions.group];
        }

        let params = {};

        dataGridExtension.customStoreRequestParams.forEach(i => {
          if (i in loadOptions && dataGridExtension.isNotEmpty(loadOptions[i])) {
            params[i] = loadOptions[i];
          }
        });

        return postWithToken(url, params)
          .then(response => response.json())
          .then((data) => {
            return {
              data: data.data,
              totalCount: data.totalCount,
            };
          })
          .catch((error) => {
            appNotify(`Failed to fetch Import Opportunity History: ${error.message}`, 'error');
            return { data: [], totalCount: 0 };
          })
          .finally(() => {
            setLoading({ isLoading: false, message: null });
          })
      },
    });

    setDataSource(store);
  }

  function createChannelLookupDataSource() {
    const store = new CustomStore({
      key: 'value',
      load: function (loadOptions) {
        const filter = dataGridExtension.getLookupFilter(loadOptions);
        const url = `/api/underwrite/import/history/channelLookup${filter}`;

        return getWithToken(url)
          .then(response => response.json())
          .then((data) => {
            return {
              data: data.data,
            };
          })
          .catch((error) => {
            appNotify(`Failed to fetch channel lookup: ${error.message}`, 'error');
          })
      }
    });

    setChannelLookupDataSource(store);
  }
  const getViewImportLogColumnRender = cell => {
    if (cell.data.completedOn || cell.data.failedOn) {
      return (<div>
        <Button
          size="small"
          color="primary"
          onClick={() => handleOpenImportLogs(cell.data)}
        >
          View Logs
        </Button>
      </div>)
    }
    return null;
  }
  const getViewWorkflowColumnRender = cell => {
    if (cell.data.completedOn) {
      return (<div>
        <Button
          size="small"
          color="primary"
          onClick={() => handleRedirectToChannelWorkflow(cell.data)}
        >
          View On Workflow
        </Button>
      </div>);
    }

    return null;
  }

  const getViewExploreColumnRender = cell => {
    if (cell.data.completedOn) {
      return (<div>
        <Button
          size="small"
          color="primary"
          onClick={() => handleRedirectToExplore(cell.data)}
        >
          View On Explore
        </Button>
      </div>);
    }

    return null;
  }
  function handleOpenImportLogs(importRecord) {
    setEventSummaryRowData({ eventType: 'Import', eventDate: importRecord.createdOn });
    fetchOpportunityHistoryLogDetails({ importId: importRecord.id });
  }

  function handleRedirectToChannelWorkflow(importRecord) {
    setChannelWorkflowGridStateUpdate({ filterValue: [['importId', '=', importRecord.id]], columnMods: [{ field: 'id', type: 'sortOrder', value: 'asc' }] }, true);
    push(CHANNEL_WORKFLOW);
  }

  function handleRedirectToExplore(importRecord) {
    GetAsgPropIdsWithImportId(importRecord.id, UNDERWRITE).then(asgPropIDs => {
      // construct filterValue
      const filterValue = ['asgPropID', 'in', asgPropIDs];
      // construct filterExpression to display on UI
      const filterExpression = [];
      asgPropIDs.forEach(id => {
        filterExpression.push(['asgPropID', '=', id]);
        filterExpression.push('or');
      });
      filterExpression.pop();
      const newFilter = { filterValue, filterExpression };
      createExploreSystemView(`Opp Import '${importRecord.name}'`, newFilter);
      push(EXPLORE);
    }).catch(error => {
      console.log(error.message);
    });
  }

  const refreshButtonClick = () => {
    createDataSource();
    sendClientLog('Info', 'Opportunity Import History - User started data fetch - refresh button');
  }

  const onToolbarPreparing = e => {
    // this adds a column chooser icon so we can customize its behaviour
    e.toolbarOptions.items.unshift({
      widget: 'dxButton',
      location: 'after',
      options: {
        icon: 'columnchooser',
        elementAttr: {
          id: 'importOpportunityHistoryGridColumnChooserBtn'
        },
        onClick: () => setIsColumnChooserVisible(true)
      }
    });
  }

  // solution from devextreme support team https://supportcenter.devexpress.com/ticket/details/t998890/datagrid-column-headerfilter-customization
  function calculateFilterExpression(
    filterValue,
    selectedFilterOperation,
    target
  ) {
    if (target === 'headerFilter') {
      return [this.dataField, selectedFilterOperation, filterValue];
    }

    return this.defaultCalculateFilterExpression.apply(this, arguments);
  }

  const columns = useMemo(() => {
    const columnList = columnSchema.map(({
      field,
      displayName,
      tooltip,
      width,
      type,
      format,
      visible,
      sortIndex,
      sortOrder,
    }) => {
      // original solutions for custom implementation from devextreme support team
      // https://supportcenter.devexpress.com/ticket/details/t1014077/datagrid-header-filter-performance-for-large-client-side-dataset
      // https://supportcenter.devexpress.com/ticket/details/t1004692/datagrid-searching-does-not-work-when-header-filter-date-values-are-ungrouped
      const headerDataSource = dataGridExtension.createHeaderDataSource('/api/underwrite/import/history', type, field, gridRef);

      if (field === 'channel') {
        return (
          <Column
            key={field}
            dataField={field}
            caption={displayName}
            tooltip={tooltip}
            minWidth={width}
            dataType={type}
            format={format}
            visible={visible !== false}
            sortIndex={sortIndex}
            sortOrder={sortOrder}
            headerFilter={{ dataSource: channelLookupDataSource }}
          >
            <Lookup
              dataSource={channelLookupDataSource}
              displayExpr="text"
              valueExpr="value"
            />
          </Column>
        )
      }

      return (
        <Column
          key={field}
          dataField={field}
          caption={displayName}
          tooltip={tooltip}
          minWidth={width}
          dataType={type}
          format={format}
          visible={visible !== false}
          sortIndex={sortIndex}
          sortOrder={sortOrder}
          calculateFilterExpression={calculateFilterExpression}
        >
          <HeaderFilter dataSource={headerDataSource} groupInterval={null} />
        </Column>
      );
    });

    columnList.push(
      (<Column
        key="viewImportLog"
        dataField="viewImportLog"
        caption={"View Logs"}
        minWidth={40}
        dataType={STRING}
        visible={true}
        cellRender={getViewImportLogColumnRender}
        allowFiltering={false}
        allowSorting={false}
      />),
      (<Column
        key="viewWorkflow"
        dataField="viewWorkflow"
        caption={VIEW_WORKFLOW}
        minWidth={40}
        dataType={STRING}
        visible={true}
        cellRender={getViewWorkflowColumnRender}
        allowFiltering={false}
        allowSorting={false}
      />),
      (<Column
        key="viewExplore"
        dataField="viewExplore"
        caption={VIEW_EXPLORE}
        minWidth={40}
        dataType={STRING}
        visible={true}
        cellRender={getViewExploreColumnRender}
        allowFiltering={false}
        allowSorting={false}
      />)
    )

    return columnList;
  }, [dataSource]);

  const fields = useMemo(() => {
    return dataGridExtension.sortFields(columnSchema);
  }, [columnSchema]);

  return (
    <div className={classes.container}>
      <div className={classes.titleContainer}>
        <div className={classes.flexContainer}>
          <Typography variant='h5' className={classes.title}>Opportunity Import History</Typography>
          <Tooltip title='Refresh Data'>
            <span>
              <IconButton disabled={loading.isLoading} onClick={refreshButtonClick} className={classes.refreshButton}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>
      <Paper className={classes.gridPaper}>
        <DataGrid
          id={gridId}
          ref={gridRef}
          persistState
          hoverStateEnabled={true}
          dataSource={dataSource}
          focusedRowEnabled={false}
          height={'calc(95vh - 140px)'}
          remoteOperations={true}
          onToolbarPreparing={onToolbarPreparing}
        >
          <HeaderFilter visible={true} search={{ enabled: true }} hideSelectAllOnSearch={false} />
          <Sorting mode={'multiple'} />
          <FilterPanel visible={true} />
          <FilterBuilder fields={fields}>
            {dataGridExtension.filterBuilderCustomOperations}
          </FilterBuilder>
          <Paging
            defaultPageSize={100}
            enabled={true}
          />
          <SearchPanel visible={true} searchVisibleColumnsOnly={true} />
          {columns}
        </DataGrid>
        {columns.map(v => {
          const target = `div#${gridId} td[aria-label='Column ${v.props.caption}']`;
          return (
            <DevExtremeTooltip
              key={v.props.dataField}
              target={target}
              showEvent="dxhoverstart"
              hideEvent="dxhoverend"
              position="top"
            >
              <p>{v.props.tooltip ?? v.props.caption}</p>
            </DevExtremeTooltip>
          )
        }
        )}
        <CustomColumnChooser
          container={`#${gridId}`}
          visible={isColumnChooserVisible}
          onHiding={() => setIsColumnChooserVisible(false)}
          columns={columns}
          gridRef={gridRef}
        />
      </Paper>
      <ViewOpportunityHistoryDetailsModal />
    </div>
  )
}

const mapStateToProps = state => {
  const underwriteState = importModuleStateSelector(state, Constants.Module.UNDERWRITE);
  return {
    appSettings: state.appState.appSettings,
    importHistoryRefresh: underwriteState.importHistoryRefresh,
  }
}

const mapDispatchToProps = {
  push,
  setChannelWorkflowGridStateUpdate,
  setIsRedirectToExplore,
  createExploreSystemView,
  appNotify,
  fetchOpportunityHistoryLogDetails,
  setEventSummaryRowData
}

export default compose(withStyles(styles), connect(mapStateToProps, mapDispatchToProps))(ImportOpportunityHistoryGrid);
